import React, { useMemo } from 'react';
import { Tooltip } from 'antd';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import {
  Monitor,
  Smartphone,
  Tablet,
  HelpCircle,
  ArrowRight,
  Files,
} from 'lucide-react';

import DataTable from '../../../../../../shared/designSystem/components/DataTable';
import AttributionIcon, {
  resolveAttributionMethod,
} from '../../../../../../shared/designSystem/components/AttributionIcon';
import SessionJourneyExpand from './SessionJourneyExpand';
import { SESSIONS_TOOLTIP } from '../../../../../../shared/constants/tooltips';

// =============================================================================
// SessionsTable (V1) — 6-column layout (down from 9). Pattern benchmarks the
// PostHog / Plausible / Hotjar session-list-with-drill-in convention:
// compact rows that scan fast, with the rich detail in the expand.
//
// Column collapse rationale:
//   • Session ID + Start Time → "Session" (stacked: time over muted ID)
//   • End Time → DROPPED (= Start + Duration; surface in expand if needed)
//   • Page Views → "Pages" (badge style)
//   • Landing Page + Exit Page → "Flow" (landing → … → exit on one line,
//     tells the entry/exit narrative)
//
// Numeric / formatting logic preserved verbatim. Sort fns kept for the
// surviving sortable columns.
// =============================================================================

const secondsToTimeText = (seconds) => {
  if (seconds < 0) return 'Invalid time';

  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts = [];

  if (hrs > 0) parts.push(`${hrs}h`);
  if (mins > 0) parts.push(`${mins}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

  return parts.join(' ');
};

const DeviceIcon = ({ device }) => {
  if (device === 'Mobile') return <Smartphone size={14} strokeWidth={1.75} />;
  if (device === 'Tablet') return <Tablet size={14} strokeWidth={1.75} />;
  return <Monitor size={14} strokeWidth={1.75} />;
};

// Compact "start → end" range. Drops the end-side date when start + end
// fall on the same calendar day (the common case — sessions usually last
// minutes). When a session spans midnight the end formats with its own
// date so the cross-day boundary stays visible. Year is always included
// per memory's date-format governance (`MMM DD, YYYY` canonical).
const DATE_TIME_FORMAT = 'MMM DD, YYYY · HH:mm:ss';
const TIME_FORMAT = 'HH:mm:ss';
const formatTimeRange = (start, end) => {
  if (!start) return '—';
  const s = moment(start);
  if (!s.isValid()) return '—';
  const e = end ? moment(end) : null;
  if (!e || !e.isValid()) return s.format(DATE_TIME_FORMAT);
  if (s.isSame(e, 'day')) {
    return `${s.format(DATE_TIME_FORMAT)} → ${e.format(TIME_FORMAT)}`;
  }
  return `${s.format(DATE_TIME_FORMAT)} → ${e.format(DATE_TIME_FORMAT)}`;
};

// Sort helpers — kept narrow / pure so the table primitive can call them
// without needing to know the row shape.
const sortMoment = (key) => (rowA, rowB) => {
  const a = rowA.original[key];
  const b = rowB.original[key];
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return moment(a).valueOf() - moment(b).valueOf();
};

const sortNumber = (key) => (rowA, rowB) =>
  (Number(rowA.original[key]) || 0) - (Number(rowB.original[key]) || 0);

const sortPageViews = (rowA, rowB) => {
  const count = (row) =>
    (row.original.journey || []).reduce(
      (acc, curr) => (curr.EVENT === 'page_view' ? acc + 1 : acc),
      0
    );
  return count(rowA) - count(rowB);
};

const SessionCell = ({ sessionId, sessionStart, sessionEnd }) => (
  <div className="campaign-sessions-v1__session-cell">
    {sessionId ? (
      <Tooltip overlayClassName="mjl-ds" title={sessionId}>
        <span className="campaign-sessions-v1__session-id">{sessionId}</span>
      </Tooltip>
    ) : (
      <span className="campaign-sessions-v1__session-id">—</span>
    )}
    <span className="campaign-sessions-v1__session-time">
      {formatTimeRange(sessionStart, sessionEnd)}
    </span>
  </div>
);

const FlowCell = ({ landing, exit }) => {
  if (!landing && !exit) return <span>—</span>;
  return (
    <div className="campaign-sessions-v1__flow-cell">
      <Tooltip overlayClassName="mjl-ds" title={landing || '—'}>
        <span className="campaign-sessions-v1__flow-page">{landing || '—'}</span>
      </Tooltip>
      <ArrowRight
        size={12}
        strokeWidth={2}
        className="campaign-sessions-v1__flow-arrow"
      />
      <Tooltip overlayClassName="mjl-ds" title={exit || '—'}>
        <span className="campaign-sessions-v1__flow-page">{exit || '—'}</span>
      </Tooltip>
    </div>
  );
};

const PagesBadge = ({ count }) => (
  <span className="campaign-sessions-v1__pages-badge">
    <Files size={12} strokeWidth={1.75} />
    <span>{count}</span>
  </span>
);

const SessionsTable = ({ data = [], loading = false, total }) => {
  const rows = useMemo(
    () => (data || []).map((d, i) => ({ ...d, __key: d.sessionId || `s-${i}` })),
    [data]
  );

  const columns = useMemo(
    () => [
      {
        id: 'session',
        accessorKey: 'sessionStart',
        header: 'Session',
        sortable: true,
        sortingFn: sortMoment('sessionStart'),
        meta: { align: 'left', width: 200 },
        cell: ({ row }) => (
          <SessionCell
            sessionId={row.original.sessionId}
            sessionStart={row.original.sessionStart}
            sessionEnd={row.original.sessionEnd}
          />
        ),
      },
      {
        id: 'durationInSeconds',
        accessorKey: 'durationInSeconds',
        header: 'Duration',
        sortable: true,
        sortingFn: sortNumber('durationInSeconds'),
        meta: { align: 'right', width: 110 },
        cell: ({ getValue }) => (
          <span className="campaign-sessions-v1__duration">
            {secondsToTimeText(getValue() || 0)}
          </span>
        ),
      },
      {
        id: 'pageViews',
        accessorKey: 'journey',
        header: 'Pages',
        sortable: true,
        sortingFn: sortPageViews,
        meta: { align: 'center', width: 90 },
        cell: ({ row }) => {
          const count = (row.original.journey || []).reduce(
            (acc, curr) => (curr.EVENT === 'page_view' ? acc + 1 : acc),
            0
          );
          return <PagesBadge count={count} />;
        },
      },
      {
        id: 'flow',
        accessorKey: 'landingPage',
        header: 'Flow',
        sortable: false,
        meta: { align: 'left' },
        cell: ({ row }) => (
          <FlowCell
            landing={row.original.landingPage}
            exit={row.original.exitPage}
          />
        ),
      },
      {
        id: 'device',
        accessorKey: 'device',
        header: 'Device',
        sortable: false,
        meta: { align: 'left', width: 120 },
        cell: ({ getValue }) => {
          const device = getValue();
          return (
            <span className="campaign-sessions-v1__device">
              <DeviceIcon device={device} />
              <span>{device}</span>
            </span>
          );
        },
      },
      {
        id: 'attributionMethod',
        accessorKey: 'attributionMethod',
        header: '',
        sortable: false,
        meta: { align: 'center', width: 60 },
        cell: ({ getValue }) => {
          const method = resolveAttributionMethod(getValue());
          return <AttributionIcon method={method || 'IP'} />;
        },
      },
    ],
    []
  );

  // Mobile card render — invoked when DataTable's `mobileBreakpoint` is met
  // (md breakpoint = below ~768px). Stacks the row content into a card so
  // the table is scannable without horizontal scroll. Kept in sync with the
  // 6-column desktop layout: top row = time + attribution, then ID, then
  // metric chips (Duration / Pages / Device), then Flow.
  const renderMobileCard = (record) => {
    const pageCount = (record.journey || []).reduce(
      (acc, curr) => (curr.EVENT === 'page_view' ? acc + 1 : acc),
      0
    );
    const method = resolveAttributionMethod(record.attributionMethod);
    return (
      <>
        <div className="campaign-sessions-v1__card-head">
          {record.sessionId ? (
            <Tooltip overlayClassName="mjl-ds" title={record.sessionId}>
              <div className="campaign-sessions-v1__card-id">{record.sessionId}</div>
            </Tooltip>
          ) : (
            <div className="campaign-sessions-v1__card-id">—</div>
          )}
          <AttributionIcon method={method || 'IP'} />
        </div>

        <div className="campaign-sessions-v1__card-time">
          {formatTimeRange(record.sessionStart, record.sessionEnd)}
        </div>

        <div className="campaign-sessions-v1__card-chips">
          <span className="campaign-sessions-v1__card-chip">
            <span className="campaign-sessions-v1__card-chip-label">Duration</span>
            <span className="campaign-sessions-v1__card-chip-value">
              {secondsToTimeText(record.durationInSeconds || 0)}
            </span>
          </span>
          <span className="campaign-sessions-v1__card-chip">
            <span className="campaign-sessions-v1__card-chip-label">Pages</span>
            <span className="campaign-sessions-v1__card-chip-value">{pageCount}</span>
          </span>
          <span className="campaign-sessions-v1__card-chip">
            <span className="campaign-sessions-v1__card-chip-label">Device</span>
            <span className="campaign-sessions-v1__card-chip-value">
              <DeviceIcon device={record.device} />
              <span style={{ marginLeft: 4 }}>{record.device || '—'}</span>
            </span>
          </span>
        </div>

        <div className="campaign-sessions-v1__card-flow">
          <Tooltip overlayClassName="mjl-ds" title={record.landingPage || '—'}>
            <span className="campaign-sessions-v1__card-flow-page">
              {record.landingPage || '—'}
            </span>
          </Tooltip>
          <ArrowRight
            size={12}
            strokeWidth={2}
            className="campaign-sessions-v1__card-flow-arrow"
          />
          <Tooltip overlayClassName="mjl-ds" title={record.exitPage || '—'}>
            <span className="campaign-sessions-v1__card-flow-page">
              {record.exitPage || '—'}
            </span>
          </Tooltip>
        </div>
      </>
    );
  };

  return (
    <section className="campaign-overview__creatives">
      <header className="campaign-overview__creatives-head">
        <div className="campaign-overview__creatives-eyebrow mono-eyebrow">
          Sessions
          <Tooltip overlayClassName="mjl-ds" title={SESSIONS_TOOLTIP}>
            <HelpCircle
              size={14}
              strokeWidth={1.75}
              style={{ marginLeft: 6, color: '#94a3b8', verticalAlign: '-2px' }}
            />
          </Tooltip>
        </div>
      </header>

      <div className="campaign-overview__creatives-body">
        <div className="campaign-overview__creatives-table">
          <DataTable
            data={rows}
            columns={columns}
            loading={loading}
            pageSize={10}
            pageSizeOptions={[10, 20, 50, 100]}
            mobileBreakpoint="md"
            renderMobileCard={renderMobileCard}
            totalRows={total}
            className="campaign-sessions-v1__sessions-table"
            expandable={{
              isRowExpandable: (record) =>
                Boolean(
                  record &&
                    ((record.impressionsList && record.impressionsList.length) ||
                      (record.journey && record.journey.length))
                ),
              renderExpandedRow: (record) => (
                <SessionJourneyExpand
                  impressions={record.impressionsList || []}
                  journey={record.journey || []}
                  sessionStart={record.sessionStart}
                  sessionEnd={record.sessionEnd}
                  durationInSeconds={record.durationInSeconds}
                  landingPage={record.landingPage}
                  exitPage={record.exitPage}
                  device={record.device}
                />
              ),
            }}
            emptyState="No sessions to show."
          />
        </div>
      </div>
    </section>
  );
};

export default withRouter(SessionsTable);
